import React from 'react';
import { FlagIcon } from '../utils/flag-icons';
import { calculateTextColor } from '../utils/custom-flag-utils';

// Define standard size and style for all indicators
// Mobile: 50% smaller (px-2 py-0.5 text-xs), Desktop: original size (px-3 py-1 text-sm)
export const indicatorBaseStyle = "inline-flex items-center px-2 py-0.5 text-xs sm:px-3 sm:py-1 sm:text-sm rounded-md";

export const WorkOrderBadge = ({ children, className = '', style, title, ariaLabel, ...props }) => (
  <span
    className={`${indicatorBaseStyle} whitespace-nowrap font-semibold border border-transparent ${className}`}
    style={style}
    title={title}
    aria-label={ariaLabel}
    {...props}
  >
    {children}
  </span>
);

export const WorkOrderUrgentBadge = () => (
  <WorkOrderBadge className="bg-red-100 text-red-800">
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    </svg>
    Urgent
  </WorkOrderBadge>
);

// Helper to format area name: remove underscores and capitalize each word
const formatAreaName = (area) => {
  if (!area) return '';
  return area
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// UI indicator components related to jobs

export const FlipIndicator = ({ job }) => (
  <span className={`${indicatorBaseStyle} bg-red-600 text-white`}>
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
    </svg>
    <span>Flip</span>
    {job?.flipOverridden && (
      <span className="ml-1 text-xs text-red-200" title="Manually overridden by admin">*</span>
    )}
  </span>
);

export const WalkthroughIndicator = () => (
  <span className={`${indicatorBaseStyle} bg-blue-100 text-blue-800`}>
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
    </svg>
    <span>Walkthrough</span>
  </span>
);

export const LateCheckoutIndicator = () => (
  <span className={`${indicatorBaseStyle} bg-yellow-100 text-yellow-800`}>
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
    </svg>
    <span>Late Checkout</span>
  </span>
);

export const EarlyCheckinIndicator = () => (
  <span className={`${indicatorBaseStyle} bg-green-100 text-green-800`} title="Guest checking in earlier than scheduled">
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
    </svg>
    <span>Early Check-in</span>
  </span>
);

export const CustomJobIndicator = () => (
  <span className={`${indicatorBaseStyle} bg-purple-100 text-purple-800`}>
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" />
    </svg>
    <span>Custom</span>
  </span>
);

export const DateOverrideIndicator = () => (
  <span className={`${indicatorBaseStyle} bg-amber-100 text-amber-800`} title="Date has been manually changed">
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
    </svg>
    <span>Date Changed</span>
  </span>
);



export const OwnerArrivalIndicator = () => (
  <span className={`${indicatorBaseStyle} bg-purple-100 text-purple-800`} title="Owner arriving next">
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
    </svg>
    <span>Owner Arrival</span>
  </span>
);

export const OwnerDepartureIndicator = () => (
  <span className={`${indicatorBaseStyle} bg-indigo-100 text-indigo-800`} title="Owner just checked out">
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
    </svg>
    <span>Owner Departure</span>
  </span>
);

export const OwnerBlockIndicator = () => (
  <span className={`${indicatorBaseStyle} bg-slate-100 text-slate-800`} title="Owner block – reservation is for owner use">
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
    </svg>
    <span>Owner Block</span>
  </span>
);

export const MaintenanceBlockIndicator = () => (
  <span className={`${indicatorBaseStyle} bg-orange-100 text-orange-800`} title="Maintenance block – property is blocked for maintenance">
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
    </svg>
    <span>Maintenance</span>
  </span>
);

export const MaintenanceStartIndicator = () => (
  <span className={`${indicatorBaseStyle} bg-orange-100 text-orange-800`} title="Maintenance period starting">
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
    </svg>
    <span>Maintenance Start</span>
  </span>
);

export const MaintenanceEndingIndicator = () => (
  <span className={`${indicatorBaseStyle} bg-orange-100 text-orange-800`} title="Maintenance period ending">
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
    <span>Maintenance Ending</span>
  </span>
);

export const ReadyForInspectionIndicator = () => (
  <span className={`${indicatorBaseStyle} bg-indigo-100 text-indigo-800`} title="Cleaning job is completed and ready for inspection">
    <svg className="h-6 w-6 sm:h-6 sm:w-6 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
    </svg>
    <span>Clean Completed</span>
  </span>
);

export const NeedsInspectionIndicator = () => (
  <span className={`${indicatorBaseStyle} bg-green-100 text-green-800`} title="This job needs inspection">
    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
    </svg>
    <span>Needs Inspection</span>
  </span>
);

export const JobCategoryPill = ({ serviceType, category, cleaningType }) => {
  // PERFORMANCE FIX: Use serviceType data directly from job query instead of making individual GraphQL calls
  // This eliminates the N+1 query problem that was causing hundreds of network requests
  
  let title = "";
  let color = "#777777";
  
  // Use serviceType data directly from the job query (already fetched in bulk)
  if (serviceType?.title) {
    title = serviceType.title;
    color = serviceType.color || color;
  }
  // Fallback to service type ID if available
  else if (serviceType?.id) {
    const serviceTypeId = typeof serviceType === 'string' ? serviceType : serviceType.id;
    title = `Type: ${serviceTypeId.slice(0, 8)}...`;
  }
  // Final fallback
  else {
    title = "Unknown";
  }
  
  // Basic styling with performance-optimized color handling
  const bgColor = color && color !== "#777777" ? `${color}20` : '#f3f4f6';
  const textColor = color && color !== "#777777" ? color : '#4b5563';
  
  return (
    <span 
      className={`${indicatorBaseStyle}`}
      style={{ 
        backgroundColor: bgColor,
        color: textColor,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
      }}
      title={`Service Type: ${title}`}
    >
      {title}
    </span>
  );
};

export const JobStatusIndicator = ({ status }) => {
  // Don't render anything for PENDING or IN_PROGRESS status
  // IN_PROGRESS will be shown as a tab at the bottom of the card
  if (status === 'PENDING' || status === 'IN_PROGRESS') return null;
  
  const getStatusStyle = () => {
    switch (status) {
      case 'READY_FOR_INSPECTION':
        return 'bg-green-100 text-green-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'CANCELED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Render appropriate icon based on status
  const renderIcon = () => {
    if (status === 'COMPLETED' || status === 'READY_FOR_INSPECTION') {
      return (
        <svg className="mr-1.5 h-3 w-3 sm:h-4 sm:w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      );
    }
    
    if (status === 'CANCELED') {
      return (
        <svg className="mr-1.5 h-3 w-3 sm:h-4 sm:w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      );
    }
    
    return null;
  };
  
  return (
    <span className={`${indicatorBaseStyle} ${getStatusStyle()}`}>
      {renderIcon()}
      {status === 'READY_FOR_INSPECTION' ? 'Ready for Inspection' : status.replace(/_/g, ' ')}
    </span>
  );
};

export const AreaIndicator = ({ area }) => {
  if (!area) return null;
  
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs sm:px-3 sm:py-1 bg-gray-100 text-gray-600" title="Property Area">
      <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
      </svg>
      <span className="text-gray-500">{formatAreaName(area)}</span>
    </span>
  );
};

// Custom Flag Indicators - matches exact styling of existing indicators
export const CustomFlagIndicator = ({ flag, flagTemplates = [] }) => {
  if (!flag) return null;

  // If flag has templateId but template relation isn't loaded, try to look it up
  let template = flag.template;
  if (!template && flag.templateId && flagTemplates.length > 0) {
    template = flagTemplates.find(t => t.id === flag.templateId);
  }

  // If flag has a template, ALWAYS use template's styling and icon (template is source of truth)
  if (template) {
    // Template is available - use it as the source of truth
    // Use exact template values - no fallbacks to ensure correct display
    const backgroundColor = template.backgroundColor;
    const textColor = template.textColor || calculateTextColor(backgroundColor);
    const iconName = template.iconName;
    
    return (
      <WorkOrderBadge
        style={{
          backgroundColor,
          color: textColor,
          border: `1px solid ${backgroundColor}`
        }}
        title={`Flag: ${flag.flagValue}`}
      >
        {iconName && (
          <FlagIcon 
            iconName={iconName} 
            className="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" 
            color={textColor}
          />
        )}
        <span>{flag.flagValue}</span>
      </WorkOrderBadge>
    );
  }

  // Get the exact same styling as existing indicators (for legacy flags without templates)
  const getIndicatorStyle = (flagType, flagValue) => {
    // Handle system indicators with exact same styling as existing indicators
    if (flagType === 'system') {
      switch (flagValue) {
        case 'Flip':
          return 'bg-red-600 text-white';
        case 'Late Checkout':
          return 'bg-yellow-100 text-yellow-800';
        case 'Early Check-in':
          return 'bg-green-100 text-green-800';
        case 'Date Changed':
          return 'bg-amber-100 text-amber-800';
        case 'Owner Arrival':
          return 'bg-purple-100 text-purple-800';
        case 'Owner Departure':
          return 'bg-indigo-100 text-indigo-800';
        case 'Maintenance Start':
          return 'bg-orange-100 text-orange-800';
        case 'Maintenance Ending':
          return 'bg-orange-100 text-orange-800';
        case 'Ready for Inspection':
          return 'bg-indigo-100 text-indigo-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    }
    
    // Handle custom flag types with appropriate colors
    switch (flagType) {
      case 'priority':
        return 'bg-red-100 text-red-800';
      case 'status':
        return 'bg-blue-100 text-blue-800';
      case 'category':
        return 'bg-green-100 text-green-800';
      case 'maintenance':
        return 'bg-orange-100 text-orange-800';
      case 'guest':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get the exact same icon as existing indicators
  const getIcon = (flagType, flagValue) => {
    // Handle system indicators with exact same icons as existing indicators
    if (flagType === 'system') {
      switch (flagValue) {
        case 'Flip':
          return (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
          );
        case 'Late Checkout':
          return (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
          );
        case 'Early Check-in':
          return (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
          );
        case 'Date Changed':
          return (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
          );
        case 'Owner Arrival':
          return (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
            </svg>
          );
        case 'Owner Departure':
          return (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
            </svg>
          );
        case 'Maintenance Start':
          return (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
          );
        case 'Maintenance Ending':
          return (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          );
        case 'Ready for Inspection':
          return (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 9.586 7.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          );
        default:
          return (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          );
      }
    }
    
    // Handle custom flag types
    switch (flagType) {
      case 'priority':
        return (
          <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'status':
        return (
          <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 9.586 7.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'category':
        return (
          <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
        );
      case 'maintenance':
        return (
          <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
          </svg>
        );
      case 'guest':
        return (
          <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  if (flag.flagColor) {
    const textColor = calculateTextColor(flag.flagColor);

    return (
      <WorkOrderBadge
        style={{
          backgroundColor: flag.flagColor,
          color: textColor,
          border: `1px solid ${flag.flagColor}`
        }}
        title={`Flag: ${flag.flagValue}`}
      >
        {getIcon(flag.flagType, flag.flagValue)}
        <span>{flag.flagValue}</span>
      </WorkOrderBadge>
    );
  }

  const indicatorStyle = getIndicatorStyle(flag.flagType, flag.flagValue);

  return (
    <WorkOrderBadge
      className={indicatorStyle}
      title={`Custom ${flag.flagType} flag: ${flag.flagValue}`}
    >
      {getIcon(flag.flagType, flag.flagValue)}
      <span>{flag.flagValue}</span>
    </WorkOrderBadge>
  );
};
